import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import CardCheckbox from "components/cardCheckbox";
import useEditService from "../../../../hooks/useEditService";
import Select from "components/select";
import RateTypesInputs from "./rateTypeInputs";
import { ReactComponent as AddCircleIcon } from "images/auth/add-circle.svg";
import { ReactComponent as TrashIcon } from "images/trash.svg";
import {
  INTERPRETATION_SERVICE_NAME,
  LANGUAGE_MODEL_NONE,
  LANGUAGE_MODEL_PAIR,
} from "utils/constantValues";

function ServiceForm({
  setValue = () => {},
  register = () => {},
  unregister = () => {},
  watch,
  errors,
  data = {},
  label = "",
  defaultServices = [],
  defaultServicesRates = {},
  isVendor = false,
}) {
  const [t] = useTranslation();
  const { services = [], languages = [] } = data;

  const languagesOptions = languages.map((elem) => ({
    ...elem,
    label: elem.title,
    value: elem.id,
  }));
  const {
    activeService,
    serviceRates,
    removeService,
    addService,
    addLanguages,
    removeLanguage,
    onHandleLanguageChange,
    onHandleRateInputChange,
    getMaxPrice,
  } = useEditService({
    defaultServices,
    defaultServicesRates,
  });

  return (
    <div className="flex flex-col justify-center gap-[24px] self-stretch">
      <div className="flex flex-wrap gap-[14px] items-center ">
        {services.map((service) => {
          return (
            <Fragment key={service.title}>
              <CardCheckbox
                title={t(service.title)}
                isActive={activeService.some((elem) => elem.id === service.id)}
                onClick={() => {
                  if (activeService.some((elem) => elem.id === service.id)) {
                    removeService(service, setValue, unregister);
                  } else {
                    addService(service, setValue);
                  }
                }}
                containerClassName="flex gap-[5px] items-center  self-stretch py-[12px] px-[10px] cursor-pointer"
                titleClassName="text-gray-700 font-semibold text-[14px] not-italic w-full"
              />
            </Fragment>
          );
        })}
      </div>
      <div className="bg-[#E4E4E7] h-[1px] w-full" />
      <div className="flex flex-col gap-[24px] self-stretch">
        {Object.keys(serviceRates).map((serviceId, serviceIndex) => {
          const service = services.find((service) => service.id === serviceId);

          const languagePairsIds = Object.keys(serviceRates[serviceId]);
          const isInterpretation =
            service.title === INTERPRETATION_SERVICE_NAME;
          return (
            <div
              className="flex flex-col gap-[16px]"
              key={`${serviceIndex + serviceId}`}
            >
              <div className="flex flex-col gap-[4px] self-stretch">
                <div className="text-[#1D1B22] text-[24px] font-semibold">
                  {t(service.title)}
                </div>
              </div>

              {languagePairsIds.map((langPairKey, langPairIndex) => {
                const serviceRatesPerLanPair =
                  serviceRates[service.id][langPairKey] || [];

                const isLimitLanguageError =
                  isVendor && languagePairsIds.length >= 10;

                const showLanguages =
                  service.languageModel !== LANGUAGE_MODEL_NONE;

                const isPair = service.languageModel === LANGUAGE_MODEL_PAIR;

                const languageFrom = isPair
                  ? watch(`${serviceId}-${langPairKey}-from`)
                    ? languagesOptions.find(
                        (elem) =>
                          elem.id === watch(`${serviceId}-${langPairKey}-from`)
                      )
                    : null
                  : false;

                const languageTo = showLanguages
                  ? watch(`${serviceId}-${langPairKey}-to`)
                    ? languagesOptions.find(
                        (elem) =>
                          elem.id === watch(`${serviceId}-${langPairKey}-to`)
                      )
                    : null
                  : false;

                const getMaxCeilingPrice = showLanguages
                  ? getMaxPrice({
                      isPair,
                      languageFrom,
                      languageTo,
                      service,
                    })
                  : false;

                const parseMaxPrice = getMaxCeilingPrice
                  ? !Number.isNaN(Number(getMaxCeilingPrice))
                    ? Number(getMaxCeilingPrice)
                    : false
                  : false;

                return (
                  <div
                    className="flex flex-col gap-[16px]"
                    key={`${langPairIndex + langPairKey}`}
                  >
                    {showLanguages && (
                      <div className="flex justify-between items-center self-stretch pt-[10px]">
                        <div className="text-[#51525C] font-semibold text-[16px]">{`Skill #${
                          langPairIndex + 1
                        }`}</div>

                        {langPairIndex > 0 && (
                          <TrashIcon
                            className="cursor-pointer  object-cover w-full max-w-[25px]"
                            onClick={() => {
                              if (isPair) {
                                unregister(`${serviceId}-${langPairKey}-from`);
                              }
                              unregister(`${serviceId}-${langPairKey}-to`);
                              serviceRates[serviceId][langPairKey].forEach(
                                (rate, rateIndex) => {
                                  unregister(
                                    `rate-${serviceId}-${langPairKey}-${rateIndex}-${rate.rateId}-${rate.fromLanguage}-${rate.toLanguage}`
                                  );
                                  unregister(
                                    `capacity-${serviceId}-${langPairKey}-${rateIndex}-${rate.rateId}-${rate.fromLanguage}-${rate.toLanguage}`
                                  );
                                }
                              );

                              removeLanguage(serviceId, langPairKey, setValue);
                            }}
                          />
                        )}
                      </div>
                    )}

                    {showLanguages && (
                      <div
                        className={`grid gap-[16px] w-full ${
                          isPair ? "grid-cols-2" : "grid-cols-1"
                        }`}
                      >
                        {isPair && (
                          <Select
                            label={t("languageFrom")}
                            placeholder={t("languagesPlaceholder")}
                            name={`${serviceId}-${langPairKey}-from`}
                            value={languageFrom}
                            onChange={(e = {}) => {
                              onHandleLanguageChange(
                                e.value,
                                serviceId,
                                "fromLanguage",
                                langPairKey,
                                setValue,
                                unregister
                              );
                              setValue(
                                `${serviceId}-${langPairKey}-from`,
                                e.value
                              );
                            }}
                            register={register}
                            registerArgs={{
                              required: t("errorField", {
                                field: t("languageFrom"),
                              }),
                            }}
                            errorMessage={
                              errors[`${serviceId}-${langPairKey}-from`]
                                ? errors[`${serviceId}-${langPairKey}-from`]
                                    .message
                                : ""
                            }
                            isRequired={true}
                            options={languagesOptions}
                          />
                        )}

                        <Select
                          label={t("languageTo")}
                          placeholder={t("languagesPlaceholder")}
                          name={`${serviceId}-${langPairKey}-to`}
                          value={languageTo}
                          onChange={(e = {}) => {
                            onHandleLanguageChange(
                              e.value,
                              serviceId,
                              "toLanguage",
                              langPairKey,
                              setValue,
                              unregister
                            );

                            setValue(`${serviceId}-${langPairKey}-to`, e.value);
                          }}
                          register={register}
                          registerArgs={{
                            required: t("errorField", {
                              field: t("languageTo"),
                            }),
                          }}
                          errorMessage={
                            errors[`${serviceId}-${langPairKey}-to`]
                              ? errors[`${serviceId}-${langPairKey}-to`].message
                              : ""
                          }
                          isRequired={true}
                          options={languagesOptions}
                        />
                      </div>
                    )}
                    {/* rates here */}

                    <div
                      className={`grid gap-[16px] ${
                        isInterpretation ? "grid-cols-2" : "grid-cols-1"
                      }`}
                    >
                      {serviceRatesPerLanPair
                        .sort((a, b) => {
                          if (isInterpretation) {
                            const hasOnlineA = t(
                              `subTitle-${a.serviceName}-${a.title}`
                            ).includes("online");

                            const hasOnlineB = t(
                              `subTitle-${b.serviceName}-${b.title}`
                            ).includes("online");
                            const hasOnSiteA = t(
                              `subTitle-${a.serviceName}-${a.title}`
                            ).includes("On-site");

                            const hasOnSiteB = t(
                              `subTitle-${b.serviceName}-${b.title}`
                            ).includes("On-site");

                            if (hasOnlineA && !hasOnlineB) return 1;
                            if (!hasOnlineA && hasOnlineB) return -1;

                            if (hasOnSiteA && !hasOnSiteB) return 1;
                            if (!hasOnSiteA && hasOnSiteB) return -1;
                          }
                          return 0;
                        })

                        .map((rateType, rateIndex) => {
                          const showDisclaimer =
                            !Number.isNaN(Number(rateType?.rate)) &&
                            typeof parseMaxPrice === "number"
                              ? Number(rateType?.rate) > parseMaxPrice
                              : false;
                          return (
                            <Fragment
                              key={`${
                                serviceId +
                                langPairIndex +
                                langPairKey +
                                rateIndex
                              }`}
                            >
                              {isInterpretation ? (
                                <div className="grid gap-[8px] items-end">
                                  <div className="text-[#A0A0AB] font-semibold text-[16px] mb-[-16px] mt-[16px]">
                                    {rateIndex === 0
                                      ? t(t("Online"))
                                      : rateIndex === 4
                                      ? t("On-site")
                                      : ""}
                                  </div>

                                  <RateTypesInputs
                                    rateType={rateType}
                                    onHandleChange={onHandleRateInputChange}
                                    languagePairKey={langPairKey}
                                    langPairIndex={langPairIndex}
                                    serviceId={serviceId}
                                    register={register}
                                    rateIndex={rateIndex}
                                    setValue={setValue}
                                    showDisclaimer={showDisclaimer}
                                    serviceRatesPerLanPair={
                                      serviceRatesPerLanPair
                                    }
                                    isCharacter={
                                      languageFrom?.isCharacter ??
                                      languageTo?.isCharacter
                                    }
                                  />
                                </div>
                              ) : (
                                <RateTypesInputs
                                  rateType={rateType}
                                  onHandleChange={onHandleRateInputChange}
                                  languagePairKey={langPairKey}
                                  langPairIndex={langPairIndex}
                                  serviceId={serviceId}
                                  register={register}
                                  rateIndex={rateIndex}
                                  setValue={setValue}
                                  showDisclaimer={showDisclaimer}
                                  isCharacter={
                                    languageFrom?.isCharacter ??
                                    languageTo?.isCharacter
                                  }
                                  serviceRatesPerLanPair={
                                    serviceRatesPerLanPair
                                  }
                                />
                              )}
                            </Fragment>
                          );
                        })}
                    </div>

                    {showLanguages &&
                      langPairIndex === languagePairsIds.length - 1 && (
                        <div className="grid ">
                          {isLimitLanguageError && (
                            <div className="text-[12px] text-red font-regular">
                              {t("maximumNumberLanguageError")}
                            </div>
                          )}
                          <div
                            className={`flex gap-[8px] ${
                              isLimitLanguageError
                                ? "cursor-not-allowed"
                                : "cursor-pointer"
                            }`}
                            onClick={() => {
                              if (!isLimitLanguageError) {
                                addLanguages(service, langPairIndex, setValue);
                              }
                            }}
                          >
                            <AddCircleIcon
                              className={
                                isLimitLanguageError
                                  ? "text-gray-500 cursor-not-allowed opacity-50"
                                  : ""
                              }
                            />
                            <div
                              className={`${
                                isLimitLanguageError
                                  ? "text-gray-500 cursor-not-allowed opacity-50"
                                  : "text-primary font-bold text-[16px] not-italic"
                              }`}
                            >
                              {t("addAnotherLanguage")}
                            </div>
                          </div>
                        </div>
                      )}
                  </div>
                );
              })}
              <div className="bg-[#E4E4E7] h-[1px] w-full" />
            </div>
          );
        })}
      </div>
    </div>
  );
}

ServiceForm.propTypes = {
  register: PropTypes.func,
  setValue: PropTypes.func,
  errors: PropTypes.object,
  watch: PropTypes.func,
  data: PropTypes.object,
  getValues: PropTypes.func,
  label: PropTypes.string,
  defaultServices: PropTypes.array,
  defaultServicesRates: PropTypes.object,
  unregister: PropTypes.func,
  isVendor: PropTypes.bool,
};

export default ServiceForm;
